import { useState } from "react"
import { useIntl } from "react-intl"

import { Button, Dialog, StaffLabelTextL } from "@doktor-se/bones-ui"
import { AssignedConversation, PaymentRequired } from "@doktor-se/bones-ui/dist/web-shared/types"

import { closeConversation, setMetadataOnConversation, setMinimumRequiredCareLevel } from "api"
import { closeOptionsFeature, featureFlags, getStaffLevels, StaffLevel } from "config"
import { checkDeclineCall } from "lib/conversations"
import { useAppDispatch, useAppSelector, useClinicStaff } from "lib/hooks"
import { SearchPageConversation } from "types"

import CloseMRCL from "./components/CloseMRCL/CloseMRCL"
import CloseOptionsContent from "./components/CloseOptionsContent/CloseOptionsContent"
import ConfirmCloseContent from "./components/ConfirmCloseContent/ConfirmCloseContent"
import PaymentContent from "./components/PaymentContent/PaymentContent"

type DialogState = "combined" | "closeOptions" | "confirm"

interface CloseDialogProps {
  isOpen: boolean
  onClose: () => void
  conversation: AssignedConversation | SearchPageConversation
}
const CloseDialog = ({ isOpen, onClose, conversation }: CloseDialogProps) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const defaultRole = useAppSelector(state => state.auth.defaultRole)
  const isClinicStaff = useClinicStaff()

  const createdByStaff = conversation.createdById !== conversation.patientId

  const hasPaymentTag =
    featureFlags.has("close_tag") &&
    !!conversation.metadata?.internal &&
    createdByStaff &&
    !conversation.metadata?.paymentRequired

  const hasCloseOptions = closeOptionsFeature(defaultRole) && !conversation.closeOptionId
  const hasMRCL = featureFlags.has("mrcl")
  const isCallActive = conversation.callState !== "inactive"

  let initialState: DialogState = "confirm"
  if (hasPaymentTag && hasMRCL && hasCloseOptions) initialState = "combined"
  else if (hasPaymentTag) initialState = "combined"
  else if (hasCloseOptions) initialState = "closeOptions"

  const [dialogState, setDialogState] = useState<DialogState>(initialState)
  const [paymentTag, setPaymentTag] = useState<PaymentRequired>()
  const [closeOptionId, setCloseOptionId] = useState<string>("")

  // MRCL level state management
  const getDefaultStaffLevel = (): StaffLevel => {
    const role = defaultRole as StaffLevel
    const validStaffLevels = getStaffLevels(isClinicStaff)

    // If role is not available in the current staff type list, default to "doctor"
    if (role && !validStaffLevels.includes(role)) {
      return "doctor"
    }

    return role || "doctor"
  }

  const [selectedMrclLevel, setSelectedMrclLevel] = useState<StaffLevel>(getDefaultStaffLevel())

  const onCloseDialog = () => {
    setPaymentTag(undefined)
    setCloseOptionId("")
    setSelectedMrclLevel(getDefaultStaffLevel())
    setDialogState(initialState)
    onClose()
  }

  const onConfirm = async () => {
    const reason = checkDeclineCall(conversation)
    const metadata = { closingStaffRole: defaultRole, ...(paymentTag && { paymentRequired: paymentTag }) }
    dispatch(setMetadataOnConversation(conversation.id, metadata))

    // Send MRCL level if the feature is enabled
    if (featureFlags.has("mrcl")) {
      await dispatch(setMinimumRequiredCareLevel(conversation.id, selectedMrclLevel))
    }

    dispatch(
      closeConversation({
        conversation,
        closeOptionId: closeOptionId || undefined,
        reason
      })
    )
    onCloseDialog()
  }

  return (
    <Dialog isOpen={isOpen} onClose={onCloseDialog} closeAriaLabel={intl.formatMessage({ id: "modal.close" })}>
      {dialogState === "combined" && (
        <>
          <StaffLabelTextL style={{ marginBottom: "var(--size-400)" }}>
            {intl.formatMessage({
              id: "control.close.confirm"
            })}
          </StaffLabelTextL>

          {hasPaymentTag && (
            <>
              <PaymentContent conversation={conversation} paymentTag={paymentTag} setPaymentTag={setPaymentTag} />

              {hasMRCL && hasCloseOptions && (
                <>
                  <div
                    style={{
                      width: "432px",
                      height: "0px",
                      margin: "var(--size-400) 0",
                      border: "1px solid #EDEDED"
                    }}
                  />
                  <CloseMRCL
                    selectedLevel={selectedMrclLevel}
                    onLevelChange={setSelectedMrclLevel}
                    showHeading={false}
                  />
                </>
              )}

              <Button variant="primary" color="primary" onPress={onConfirm} isDisabled={!paymentTag} fullWidth>
                {intl.formatMessage({ id: "label.close.errand" })}
              </Button>
            </>
          )}

          {!hasPaymentTag && hasMRCL && hasCloseOptions && (
            <>
              <CloseMRCL selectedLevel={selectedMrclLevel} onLevelChange={setSelectedMrclLevel} />
              <Button variant="primary" color="primary" onPress={onConfirm} fullWidth>
                {intl.formatMessage({ id: "label.close.errand" })}
              </Button>
            </>
          )}
        </>
      )}
      {dialogState === "closeOptions" && (
        <>
          {featureFlags.has("mrcl") ? (
            <>
              <StaffLabelTextL>
                {intl.formatMessage({
                  id: "control.close.confirm"
                })}
              </StaffLabelTextL>
              <CloseMRCL selectedLevel={selectedMrclLevel} onLevelChange={setSelectedMrclLevel} />
              <Button variant="primary" color="primary" onPress={onConfirm}>
                {intl.formatMessage({ id: "label.close.errand" })}
              </Button>
            </>
          ) : (
            <CloseOptionsContent
              categoryId={conversation.categoryId}
              closeOptionId={closeOptionId}
              setCloseOptionId={setCloseOptionId}
              onConfirm={onConfirm}
            />
          )}
        </>
      )}
      {dialogState === "confirm" && <ConfirmCloseContent onConfirm={onConfirm} isCallActive={isCallActive} />}
    </Dialog>
  )
}

export default CloseDialog
